<template>
  <div class="p-6 space-y-6">
    <!-- Header. Same shape as the access-control / overview pages so
         the visual rhythm matches across the app. -->
    <div class="flex items-start justify-between gap-4">
      <div class="min-w-0">
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          <Icon name="heroicons:bell-alert" class="w-7 h-7 text-purple-500" />
          Notifications
        </h1>
        <p class="text-gray-600 dark:text-gray-400">
          Cost anomalies, long-running queries, and account messages — the same items you see in the bell, with full history and filtering.
        </p>
      </div>
      <div v-if="unreadCount" class="flex flex-col items-end gap-1 shrink-0">
        <button
          @click="markAllRead"
          class="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium shadow-sm transition-colors"
        >
          <Icon name="heroicons:check" class="w-4 h-4" />
          Mark all read ({{ unreadCount }})
        </button>
      </div>
    </div>

    <!-- Filter bar — type chip + severity chip + unread toggle. Plain
         Tailwind chips rather than the DashboardFilterBar so the page
         doesn't take an unrelated dependency on date-range plumbing. -->
    <div class="flex flex-wrap items-center gap-2 text-sm">
      <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 mr-1">Filter:</span>
      <button
        v-for="t in typeOptions"
        :key="t.value"
        @click="typeFilter = t.value"
        :class="typeFilter === t.value ? 'bg-purple-600 text-white' : 'bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/10'"
        class="px-3 py-1 rounded-full text-xs font-medium transition-colors"
      >
        {{ t.label }}
      </button>
      <span class="mx-2 h-4 w-px bg-gray-200 dark:bg-white/10"></span>
      <button
        v-for="s in severityOptions"
        :key="s.value"
        @click="severityFilter = s.value"
        :class="severityFilter === s.value ? 'bg-purple-600 text-white' : 'bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/10'"
        class="px-3 py-1 rounded-full text-xs font-medium transition-colors capitalize"
      >
        {{ s.label }}
      </button>
      <span class="mx-2 h-4 w-px bg-gray-200 dark:bg-white/10"></span>
      <label class="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
        <input v-model="onlyUnread" type="checkbox" class="rounded border-gray-300 dark:border-white/20 text-purple-600" />
        Unread only
      </label>
    </div>

    <!-- Loading / empty / list states — same `glass-panel` chrome as
         the rest of the app so /notifications doesn't look out of place. -->
    <div v-if="!hydrated" class="glass-panel py-16">
      <PulsarLoader label="Loading notifications…" size="md" />
    </div>
    <div v-else-if="!filtered.length" class="glass-panel p-12 text-center">
      <Icon name="heroicons:check-circle" class="w-12 h-12 mx-auto text-gray-300 dark:text-white/20 mb-2" />
      <p class="text-base text-gray-700 dark:text-gray-300 mb-1">{{ emptyHeadline }}</p>
      <p class="text-sm text-gray-500 dark:text-gray-400">{{ emptySubtext }}</p>
    </div>
    <div v-else class="glass-panel overflow-hidden">
      <!-- Each row is a button so keyboard nav + mark-read-on-activate
           works without extra ARIA wiring. -->
      <button
        v-for="n in filtered"
        :key="n.id"
        @click="onActivate(n)"
        :class="[
          'w-full text-left px-5 py-4 border-b border-gray-100 dark:border-white/5 last:border-b-0',
          'hover:bg-gray-50 dark:hover:bg-white/5 transition-colors',
          isUnread(n) ? 'bg-purple-50/40 dark:bg-purple-500/5' : '',
        ]"
      >
        <div class="flex items-start gap-3">
          <span :class="['w-2 h-2 rounded-full mt-2 shrink-0', dotClass(n)]"></span>
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2 flex-wrap">
              <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ n.title }}</p>
              <span :class="badgeForSeverity(n.severity)" class="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide">
                {{ n.severity }}
              </span>
              <span class="text-[10px] uppercase tracking-wider text-gray-500">{{ typeLabel(n.type) }}</span>
            </div>
            <p class="text-sm text-gray-700 dark:text-gray-300 mt-1 leading-relaxed">{{ n.body }}</p>
            <p class="text-xs text-gray-400 mt-1.5">{{ formatRelative(n.createdAt) }}{{ formatAbsolute(n.createdAt) }}</p>
          </div>
          <button
            @click.stop="dismiss(n.id)"
            class="p-1.5 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 shrink-0"
            title="Dismiss"
          >
            <Icon name="heroicons:x-mark" class="w-4 h-4" />
          </button>
        </div>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AppNotification, NotificationSeverity, NotificationType } from '~/stores/notificationsStore'
import PulsarLoader from '~/components/common/PulsarLoader.vue'

definePageMeta({
  authenticated: true,
})

const router = useRouter()
const {
  visible,
  unread,
  unreadCount,
  hydrated,
  markRead,
  markAllRead,
  dismiss,
} = useNotifications()

// ---------------------------------------------------------------------------
// Filters. All driven by reactive refs so toggling pills updates the
// list without re-rendering the page. URL sync isn't needed yet — the
// page is meant for browsing, not for sharable filtered links.
// ---------------------------------------------------------------------------

const typeFilter = ref<'all' | NotificationType>('all')
const severityFilter = ref<'all' | NotificationSeverity>('all')
const onlyUnread = ref(false)

const typeOptions: { value: 'all' | NotificationType, label: string }[] = [
  { value: 'all', label: 'All types' },
  { value: 'cost_anomaly', label: 'Cost anomalies' },
  { value: 'long_running_query', label: 'Long-running queries' },
  { value: 'account_message', label: 'Account messages' },
]
const severityOptions: { value: 'all' | NotificationSeverity, label: string }[] = [
  { value: 'all', label: 'All severities' },
  { value: 'critical', label: 'Critical' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' },
]

const filtered = computed(() => {
  return visible.value.filter((n) => {
    if (typeFilter.value !== 'all' && n.type !== typeFilter.value) return false
    if (severityFilter.value !== 'all' && n.severity !== severityFilter.value) return false
    if (onlyUnread.value && !isUnread(n)) return false
    return true
  })
})

const emptyHeadline = computed(() => {
  if (typeFilter.value === 'all' && severityFilter.value === 'all' && !onlyUnread.value) {
    return 'You have no notifications yet.'
  }
  return 'No notifications match the current filters.'
})
const emptySubtext = computed(() => {
  if (typeFilter.value === 'all' && severityFilter.value === 'all' && !onlyUnread.value) {
    return 'Cost anomalies, long-running queries, and admin messages will appear here as they happen.'
  }
  return 'Try clearing the filters above or switching the type / severity selection.'
})

function isUnread(n: AppNotification): boolean {
  return unread.value.includes(n)
}

async function onActivate(n: AppNotification) {
  await markRead(n.id)
  if (n.link?.path) {
    router.push({ path: n.link.path, query: n.link.query || {} })
  }
}

// ---------------------------------------------------------------------------
// Display helpers — kept in lock-step with the bell dropdown so the
// two surfaces look the same. (Not deduped into a shared module yet
// because they're 4 small functions; can move if a third surface
// shows up.)
// ---------------------------------------------------------------------------

function dotClass(n: AppNotification): string {
  if (!isUnread(n)) return 'bg-gray-300 dark:bg-white/20'
  return ({
    critical: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500',
  })[n.severity]
}
function badgeForSeverity(s: NotificationSeverity): string {
  return ({
    critical: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300',
    warning: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-300',
    info: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300',
  })[s]
}
function typeLabel(t: NotificationType): string {
  return ({
    cost_anomaly: 'Cost anomaly',
    long_running_query: 'Long-running query',
    account_message: 'Account message',
  })[t] || 'Notification'
}
function formatRelative(ts: { toDate?: () => Date } | null): string {
  if (!ts || typeof ts.toDate !== 'function') return 'just now'
  const ms = Date.now() - ts.toDate().getTime()
  if (ms < 60_000) return 'just now'
  const min = Math.floor(ms / 60_000)
  if (min < 60) return `${min} min ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr} hr ago`
  const day = Math.floor(hr / 24)
  if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`
  return formatInUserTz(ts.toDate(), { dateOnly: true })
}
function formatAbsolute(ts: { toDate?: () => Date } | null): string {
  if (!ts || typeof ts.toDate !== 'function') return ''
  return ` · ${formatInUserTz(ts.toDate(), { withSeconds: true })}`
}

const { formatInUserTz } = useUserTimezone()
</script>
